function normalizeKeys(obj) {
  const keyMap = {
    backgroundColor: 'background-color',
    backgroundImage: 'background-image',
    brand100: 'primary-100',
    brand700: 'primary-700',
    brand800: 'primary-800',
    brand900: 'primary-900',
    buttonBackgroundColor: 'button-background-color',
    buttonBackgroundHoverColor: 'button-background-hover-color',
    buttonBorderRadius: 'button-border-radius',
    buttonTextColor: 'button-text-color',
    buttonTextTransform: 'button-text-transform',
    checkboxHoverBorderColor: 'checkbox-hover-border-color',
    desktopHeadlineFontSize: 'desktop-headline-font-size',
    desktopHeadlineLetterSpacing: 'desktop-headline-letter-spacing',
    desktopHeadlineLineHeight: 'desktop-headline-line-height',
    desktopParagraphFontSize: 'desktop-paragraph-font-size',
    desktopParagraphLetterSpacing: 'desktop-paragraph-letter-spacing',
    desktopParagraphLineHeight: 'desktop-paragraph-line-height',
    desktopSubtextFontSize: 'desktop-subtext-font-size',
    desktopSubtextLetterSpacing: 'desktop-subtext-letter-spacing',
    desktopSubtextLineHeight: 'desktop-subtext-line-height',
    faviconUrl: 'favicon-url',
    headlineTextColor: 'headline-text-color',
    headlineTextTransform: 'headline-text-transform',
    inputActiveBorderColor: 'input-active-border-color',
    logoWidthDesktop: 'logo-width-desktop',
    logoWidthMobile: 'logo-width-mobile',
    mobileHeadlineFontSize: 'mobile-headline-font-size',
    mobileHeadlineLetterSpacing: 'mobile-headline-letter-spacing',
    mobileHeadlineLineHeight: 'mobile-headline-line-height',
    mobileParagraphFontSize: 'mobile-paragraph-font-size',
    mobileParagraphLetterSpacing: 'mobile-paragraph-letter-spacing',
    mobileParagraphLineHeight: 'mobile-paragraph-line-height',
    mobileSubtextFontSize: 'mobile-subtext-font-size',
    mobileSubtextLetterSpacing: 'mobile-subtext-letter-spacing',
    mobileSubtextLineHeight: 'mobile-subtext-line-height',
    paragraphTextColor: 'paragraph-text-color',
    paragraphTextTransform: 'paragraph-text-transform',
    selectorBackgroundColor: 'selector-background-color',
    selectorBackgroundColorDimmed: 'selector-background-color-dimmed',
    selectorBorderColor: 'selector-border-color',
    subTextColor: 'subtext-color',
    subTextTransform: 'subtext-transform',
    welcomeScreenDescription: 'welcome-screen-description',
    welcomeScreenTitle: 'welcome-screen-title',
  };

  // Helper function to convert hex to rgba with 50% opacity
  function hexToRgba(hex, alpha = 0.1) {
    if (!hex || !hex.startsWith('#')) return hex;

    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  function processKeyValue(key, value, normalizedKey) {
    const finishedObject = {};

    if (/(url|image)/.test(normalizedKey)) {
      finishedObject[normalizedKey] = `url(${value})`;
    } else if (normalizedKey === 'checkbox-hover-border-color') {
      const selectorBorderColor = obj['selectorBorderColor'] || obj['selector-border-color'];
      finishedObject[normalizedKey] = selectorBorderColor || value;
    } else {
      finishedObject[normalizedKey] = value;
    }

    if (
      'selector-background-color' in finishedObject &&
      finishedObject['selector-background-color'] &&
      finishedObject['selector-background-color'].startsWith('#')
    ) {
      finishedObject['selector-background-color-dimmed'] = hexToRgba(finishedObject['selector-background-color'], 0.1);
    }

    return finishedObject;
  }

  return Object.keys(obj).reduce((keys, key) => {
    if (!keyMap[key]) {
      return keys;
    }

    const normalizedKey = keyMap[key];
    const processedValues = processKeyValue(key, obj[key], normalizedKey);

    return { ...keys, ...processedValues };
  }, {});
}

export function applyTheme(theme) {
  const root = document.documentElement;
  Object.entries(normalizeKeys(theme)).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });
}

export function applyFontFace({ fontWoff, fontWoff2, fontWoff3, family }) {
  if (!fontWoff && !fontWoff2 && !fontWoff3) {
    console.warn('No font files provided!');

    return;
  }

  const headlineFamily = family || 'CustomHeadline';
  const paragraphFamily = family || 'CustomParagraph';
  const subTextFamily = family || 'CustomSubText';

  // Create separate font sources for each type
  const headlineSources = [];
  const paragraphSources = [];
  const subTextSources = [];

  if (fontWoff) headlineSources.push(`url('${fontWoff}') format('woff2')`);
  if (fontWoff2) paragraphSources.push(`url('${fontWoff2}') format('woff2')`);
  if (fontWoff3) subTextSources.push(`url('${fontWoff3}') format('woff2')`);

  const style = [];

  // Only create @font-face declarations when sources are available
  if (headlineSources.length > 0) {
    style.push(`
  @font-face {
    font-family: '${headlineFamily}';
    src: ${headlineSources.join(', ')};
    font-weight: normal;
    font-style: normal;
  }`);
  }

  if (paragraphSources.length > 0) {
    style.push(`
  @font-face {
    font-family: '${paragraphFamily}';
    src: ${paragraphSources.join(', ')};
    font-weight: normal;
    font-style: normal;
  }`);
  }

  if (subTextSources.length > 0) {
    style.push(`
  @font-face {
    font-family: '${subTextFamily}';
    src: ${subTextSources.join(', ')};
    font-weight: normal;
    font-style: normal;
  }`);
  }

  // Inject the font-face into the document only if we have styles to inject
  if (style.length > 0) {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = style.join('');
    document.head.appendChild(styleTag);
  }

  // Apply the fonts to CSS variables, with fallbacks to default fonts
  document.documentElement.style.setProperty('--headline-font', headlineSources.length > 0 ? headlineFamily : 'Faktum');
  document.documentElement.style.setProperty('--paragraph-font', paragraphSources.length > 0 ? paragraphFamily : '');
  document.documentElement.style.setProperty('--button-font', paragraphSources.length > 0 ? paragraphFamily : '');
  document.documentElement.style.setProperty('--subtext-font', subTextSources.length > 0 ? subTextFamily : '');
}
